// ABOUTME: Modal listing the bulk label jobs of a picking job in the echeck, with the failures per job
// ABOUTME: and a button per job that prints every label that was created again on the selected workspace.

import { Box, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import PrintIcon from '@mui/icons-material/Print';
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import MPrintedOrderLabels from "./MPrintedOrderLabels";
import { tokens } from "../../../theme";
import { useGetShippingProgressQuery } from "../../../newapi/pickingjob/pickingjobSlice";
import { usePrintOrderLabelsMutation } from "../../../newapi/order/ordersSlice";
import { showToastMessage, showToastMessageRequestError } from "../../../utils/toasts";

const MEcheckPrintJobs = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    const selectedWorkSpace = useSelector((state) => state.settings.workSpace);

    // Polls while the modal is open, so a job that is still running keeps counting
    const { data } = useGetShippingProgressQuery(props.pickingJobId, {
        skip: !props.open || !props.pickingJobId,
        pollingInterval: 5000,
        refetchOnMountOrArgChange: true,
    });

    const [reprintingJob, setReprintingJob] = useState(null);
    const [printResults, setPrintResults] = useState({ open: false, results: [], workspaceId: null });
    const [printOrderLabels] = usePrintOrderLabelsMutation();

    // Newest job first
    const jobs = [...(data?.progress?.workspaces ?? [])].reverse();

    const handlePrintAgain = async (job, index) => {
        if (reprintingJob !== null) return;

        if (!selectedWorkSpace?.id) {
            showToastMessage("error", t("select_workspace_with_label_printer"));
            return;
        }

        setReprintingJob(index);
        try {
            const response = await printOrderLabels({
                orderIds: job.labelOrderIds,
                workspaceId: selectedWorkSpace.id
            }).unwrap();

            setPrintResults({ open: true, results: response.results ?? [], workspaceId: selectedWorkSpace.id });
        } catch (err) {
            showToastMessageRequestError(t("label_print_failed"), t, err);
        } finally {
            setReprintingJob(null);
        }
    };

    return (
        <>
            <SHModal
                open={props.open}
                onClose={props.handleClose}
                sx={{ body: { minWidth: 700 } }}
            >
                <Box
                    sx={{
                        textAlign: "center",
                        paddingY: 4,
                        paddingX: 5,
                    }}
                >
                    <Subheading>{t("print_jobs")}</Subheading>
                </Box>
                <Box
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        padding: 3,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        overflowY: "auto",
                        maxHeight: "60vh"
                    }}
                >
                    {jobs.length === 0 &&
                        <Text light>{t("no_print_jobs")}</Text>
                    }
                    {jobs.map((job, index) => (
                        <Box
                            key={job.jobId ?? index}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                borderRadius: 3,
                                p: 2,
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                                    <Text bold>{job.timestamp ? format(new Date(job.timestamp), "dd-MM-yyyy HH:mm") : ""}</Text>
                                    <Text light>{t("print_job_labels_created", { done: job.done ?? 0, total: job.total ?? 0 })}</Text>
                                </Box>
                                {/* A job stored without its order ids has nothing to print again */}
                                {(job.labelOrderIds?.length ?? 0) > 0 &&
                                    <Shbutton
                                        className={"always-visible"}
                                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                        variant="contained"
                                        isLoading={reprintingJob === index}
                                        disabled={reprintingJob !== null}
                                        onClick={() => handlePrintAgain(job, index)}
                                        endIcon={<PrintIcon />}
                                    >
                                        {t("print_successful_labels_again")}
                                    </Shbutton>
                                }
                            </Box>
                            {(job.errors ?? []).map((err) => (
                                <Box
                                    key={err.id}
                                    sx={{
                                        background: colors.red[100],
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        px: 2,
                                        py: 1,
                                    }}
                                >
                                    <Text bold color={colors.red[400]} sx={{ minWidth: 100 }}>
                                        {err.barcode}
                                    </Text>
                                    <Text color={colors.red[400]}>
                                        {err.error}
                                    </Text>
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
                <Box
                    component="footer"
                    sx={{
                        width: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingX: 3,
                        paddingY: 2,
                        background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        borderBottomLeftRadius: 24,
                        borderBottomRightRadius: 24,
                    }}
                >
                    <Shbutton isCancel={true} className={"always-visible"} onClick={props.handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon />}>{t("close")}</Shbutton>
                </Box>
            </SHModal>
            <MPrintedOrderLabels
                open={printResults.open}
                handleClose={() => setPrintResults(prev => ({ ...prev, open: false }))}
                results={printResults.results}
                workspaceId={printResults.workspaceId}
            />
        </>
    );
};

export default MEcheckPrintJobs;
